import * as Sentry from "@sentry/react";
import { resolve } from "./resolve";
import { IBaseGameConfig } from "../gameLoader/types/IGameLoader.types";
import packageJson from "../../package.json";
import { getDeviceReport } from "@npm-modules/device-detector";
import { DeviceReport } from "@npm-modules/device-detector/dist/types";
import { TypeEnvConfig } from "./env.utils";

interface IParams {
    partner: string;
    gameName: string;
    demo: string;
    replayMode: string;
}

let globalConfigs: Partial<IBaseGameConfig> | null = null;

const getParams = (): Partial<IParams> => {
    try {
        const _params = new URLSearchParams(location.search.substring(1));
        const configs = globalConfigs || {};

        return {
            partner: resolve<any, string>(configs, (e) => e["partner"], _params.get("partner") || _params.get("wl") || "-"),
            gameName: resolve<any, string>(configs, (e) => e["gameName"], _params.get("gameName") || _params.get("game") || "-"),
            demo: String(resolve<any, string>(configs, (e) => e["demo"], _params.get("demo") || "false") === "1"),
            replayMode: String(/replay-/.test(resolve<any, string>(configs, (e) => e["key"], "false"))),
        };
    } catch (e) {
        console.warn(`[sentry getParams error] `, e);
        return {};
    }
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const initSentry = (envConfig: TypeEnvConfig): void => {
    const sentryConfig = envConfig.sentry;

    if (!sentryConfig || sentryConfig.enable === false) {
        console.log("[Bash] Sentry disabled via env config.");
        return;
    }

    if (Sentry.isInitialized()) {
        return;
    }

    const aliceIQUrl = envConfig.aliceIQ?.url;

    Sentry.init({
        dsn: sentryConfig.dsn,
        release: packageJson.version,
        enabled: !["localhost", "127.0.0.1"].includes(location.hostname),
        enableMetrics: sentryConfig.enableMetrics,
        sendDefaultPii: sentryConfig.sendDefaultPii,
        integrations: [Sentry.captureConsoleIntegration({ levels: sentryConfig.levels })],
        denyUrls: aliceIQUrl ? [new RegExp(escapeRegExp(aliceIQUrl))] : [],
        tracesSampleRate: sentryConfig.tracesSampleRate,
        tracePropagationTargets: ["localhost"],
        initialScope: {
            tags: getParams(),
        },
    });
};

let devicePlayerInfoSent = false;

const setGlobalConfigs = (configs: Partial<IBaseGameConfig>): void => {
    globalConfigs = configs;
};

// start() must be called again on every lobby re-enter, not just the initial load.
const createGameLoadTracker = () => {
    let startTime = Date.now();
    let sent = false;

    return {
        start: (): void => {
            startTime = Date.now();
            sent = false;
        },
        track: async (): Promise<void> => {
            if (sent) return;
            sent = true;

            try {
                const loadTimeMs: number = Date.now() - startTime;
                const sentryContext = await getSentryContext();

                if (!sentryContext.gameName) {
                    return;
                }

                Sentry.metrics.distribution("game_load_time", loadTimeMs, {
                    unit: "millisecond",
                    attributes: { ...sentryContext },
                });
                void Sentry.flush();
            } catch (e) {
                sent = false;
                console.warn(`[sentry metric "game_load_time" error] `, e);
            }
        },
    };
};

const gameLoadTracker = createGameLoadTracker();

const setSentryTags = async (): Promise<void> => {
    try {
        const tags = await getSentryContext();
        Sentry.setTags({ ...tags });
    } catch (e) {
        console.warn(`[sentry error] `, e);
    }
};

const trackDevicePlayerInfo = async (visitorId: string): Promise<void> => {
    if (devicePlayerInfoSent) return;
    devicePlayerInfoSent = true;
    try {
        const sentryContext = await getSentryContext();

        if (!sentryContext.gameName) {
            return;
        }

        Sentry.metrics.count("device_player_info", 1, {
            attributes: {
                visitorId,
                ...sentryContext,
            },
        });
        void Sentry.flush();
    } catch (e) {
        devicePlayerInfoSent = false;
        console.warn(`[sentry metric "device_player_info" error] `, e);
    }
};

const getSentryContext = async (): Promise<Partial<DeviceReport & IParams>> => {
    try {
        const deviceReport = await getDeviceReport();
        return {
            ...deviceReport,
            ...getParams(),
        };
    } catch (e) {
        console.warn(`[sentry getSentryContext error] `, e);
        return {};
    }
};

export const SentryUtils = {
    init: initSentry,
    setGlobalConfigs,
    setSentryTags,
    startGameLoadTimer: gameLoadTracker.start,
    trackGameLoadMetric: gameLoadTracker.track,
    trackDevicePlayerInfo,
};